import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { axiosGetData } from "@shared/api/clients";

import type {
  InspectionAccessibleStructure,
  InspectionCapabilitiesResponse,
  InspectionChartParams,
  InspectionChartResponse,
  InspectionDetail,
  InspectionListParams,
  InspectionListResponse,
  InspectionReport,
  InspectionSummaryResponse,
  InspectionParticipantUsersParams,
  InspectionParticipantUsersResponse,
} from "./inspection.types";

export const INSPECTION_QUERY_KEY = "inspection";

export const inspectionStructureEndpoint = (structureId: string) =>
  `v1/inspection/structures/${structureId}`;

export const inspectionQueryKeys = {
  all: [INSPECTION_QUERY_KEY] as const,
  accessibleStructures: () => [INSPECTION_QUERY_KEY, "accessible-structures"],
  capabilities: (structureId: string) => [
    INSPECTION_QUERY_KEY,
    "capabilities",
    structureId,
  ],
  summary: (structureId: string) => [INSPECTION_QUERY_KEY, "summary", structureId],
  chart: (structureId: string, params: InspectionChartParams) => [
    INSPECTION_QUERY_KEY,
    "chart",
    structureId,
    params,
  ],
  list: (structureId: string, params?: InspectionListParams) => [
    INSPECTION_QUERY_KEY,
    "list",
    structureId,
    params ?? {},
  ],
  detail: (structureId: string, inspectionId: string) => [
    INSPECTION_QUERY_KEY,
    "detail",
    structureId,
    inspectionId,
  ],
  report: (structureId: string, inspectionId: string) => [
    INSPECTION_QUERY_KEY,
    "report",
    structureId,
    inspectionId,
  ],
  participantUsers: (
    structureId: string,
    params?: InspectionParticipantUsersParams,
  ) => [INSPECTION_QUERY_KEY, "participant-users", structureId, params ?? {}],
};

/**
 * `tenantIdentifier` nunca vai no payload/query — vem sempre da sessão
 * autenticada no backend (docs/multi-tenant.md; quality-test §1.1).
 */
const cleanParams = (params: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== "",
    ),
  );

export const getInspectionCapabilities = (structureId: string) =>
  axiosGetData<InspectionCapabilitiesResponse>(
    `${inspectionStructureEndpoint(structureId)}/capabilities`,
  );

export const useInspectionCapabilitiesQuery = (
  structureId: string | undefined,
  options?: Partial<UseQueryOptions<InspectionCapabilitiesResponse, AxiosError>>,
) =>
  useQuery<InspectionCapabilitiesResponse, AxiosError>({
    queryKey: inspectionQueryKeys.capabilities(structureId ?? ""),
    queryFn: () => getInspectionCapabilities(structureId ?? ""),
    enabled: Boolean(structureId),
    ...options,
  });

export const getInspectionAccessibleStructures = () =>
  axiosGetData<InspectionAccessibleStructure[]>(
    "v1/inspection/accessible-structures",
  );

export const useInspectionAccessibleStructuresQuery = (
  options?: Partial<UseQueryOptions<InspectionAccessibleStructure[], AxiosError>>,
) =>
  useQuery<InspectionAccessibleStructure[], AxiosError>({
    queryKey: inspectionQueryKeys.accessibleStructures(),
    queryFn: getInspectionAccessibleStructures,
    ...options,
  });

export const getInspectionSummary = (structureId: string) =>
  axiosGetData<InspectionSummaryResponse>(
    `${inspectionStructureEndpoint(structureId)}/summary`,
  );

export const useInspectionSummaryQuery = (
  structureId: string | undefined,
  options?: Partial<UseQueryOptions<InspectionSummaryResponse, AxiosError>>,
) =>
  useQuery<InspectionSummaryResponse, AxiosError>({
    queryKey: inspectionQueryKeys.summary(structureId ?? ""),
    queryFn: () => getInspectionSummary(structureId ?? ""),
    enabled: Boolean(structureId),
    ...options,
  });

export const getInspectionChart = (
  structureId: string,
  params: InspectionChartParams,
) =>
  axiosGetData<InspectionChartResponse>(
    `${inspectionStructureEndpoint(structureId)}/chart`,
    cleanParams({ ...params }),
  );

export const useInspectionChartQuery = (
  structureId: string | undefined,
  params: InspectionChartParams,
  options?: Partial<UseQueryOptions<InspectionChartResponse, AxiosError>>,
) =>
  useQuery<InspectionChartResponse, AxiosError>({
    queryKey: inspectionQueryKeys.chart(structureId ?? "", params),
    queryFn: () => getInspectionChart(structureId ?? "", params),
    enabled: Boolean(structureId),
    ...options,
  });

export const getInspectionList = (
  structureId: string,
  params?: InspectionListParams,
) =>
  axiosGetData<InspectionListResponse>(
    inspectionStructureEndpoint(structureId),
    cleanParams({ ...params }),
  );

export const useInspectionListQuery = (
  structureId: string | undefined,
  params?: InspectionListParams,
  options?: Partial<UseQueryOptions<InspectionListResponse, AxiosError>>,
) =>
  useQuery<InspectionListResponse, AxiosError>({
    queryKey: inspectionQueryKeys.list(structureId ?? "", params),
    queryFn: () => getInspectionList(structureId ?? "", params),
    enabled: Boolean(structureId),
    ...options,
  });

export const getInspectionDetail = (structureId: string, inspectionId: string) =>
  axiosGetData<InspectionDetail>(
    `${inspectionStructureEndpoint(structureId)}/${inspectionId}`,
  );

export const useInspectionDetailQuery = (
  structureId: string | undefined,
  inspectionId: string | undefined,
  options?: Partial<UseQueryOptions<InspectionDetail, AxiosError>>,
) =>
  useQuery<InspectionDetail, AxiosError>({
    queryKey: inspectionQueryKeys.detail(structureId ?? "", inspectionId ?? ""),
    queryFn: () => getInspectionDetail(structureId ?? "", inspectionId ?? ""),
    enabled: Boolean(structureId) && Boolean(inspectionId),
    ...options,
  });

/** Y=9 — metadados do PDF da ficha finalizada (endpoints §10). */
export const getInspectionReport = (structureId: string, inspectionId: string) =>
  axiosGetData<InspectionReport>(
    `${inspectionStructureEndpoint(structureId)}/${inspectionId}/report`,
  );

export const useInspectionReportQuery = (
  structureId: string | undefined,
  inspectionId: string | undefined,
  options?: Partial<UseQueryOptions<InspectionReport, AxiosError>>,
) =>
  useQuery<InspectionReport, AxiosError>({
    queryKey: inspectionQueryKeys.report(structureId ?? "", inspectionId ?? ""),
    queryFn: () => getInspectionReport(structureId ?? "", inspectionId ?? ""),
    enabled: Boolean(structureId) && Boolean(inspectionId),
    ...options,
  });

/** Y=15 — usuários do tenant para o seletor de participante extra. */
export const getInspectionParticipantUsers = (
  structureId: string,
  params?: InspectionParticipantUsersParams,
) =>
  axiosGetData<InspectionParticipantUsersResponse>(
    `${inspectionStructureEndpoint(structureId)}/participant-users`,
    cleanParams({ ...params }),
  );

export const useInspectionParticipantUsersQuery = (
  structureId: string | undefined,
  params?: InspectionParticipantUsersParams,
  options?: Partial<UseQueryOptions<InspectionParticipantUsersResponse, AxiosError>>,
) =>
  useQuery<InspectionParticipantUsersResponse, AxiosError>({
    queryKey: inspectionQueryKeys.participantUsers(structureId ?? "", params),
    queryFn: () => getInspectionParticipantUsers(structureId ?? "", params),
    enabled: Boolean(structureId),
    ...options,
  });
